import { ChangeEvent, useEffect, useMemo, useState } from "react";
import Button from "react-bootstrap/Button";
import { useDispatch } from "react-redux";
import { resources } from "@constants/hardcoded-strings";
import { MESSAGING_TEMPLATE_TYPE_OPTIONS } from "@constants/messaging-template-type-options";
import {
    EMAIL_TEMPLATE_FIELDS_DCUI_FROM_EMAIL_ADDRESS_PROPERTY_NAME,
    EMAIL_TEMPLATE_FIELDS_PIN_FROM_EMAIL_ADDRESS_PROPERTY_NAME
} from "@constants/messaging-template-constants";
import {
    DEFAULT_EMAIL_FROM_ADDRESS,
    EMAIL_TEMPLATE_FIELDS_PROPERTY_TYPE_ID
} from "@constants/verification-service-details-constants";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import { ThemingConfigurationContainer } from "@containers/common/theming-configuration-container/theming-configuration-container";
import { ThemingFormContainer } from "@containers/common/theming-form-container/theming-form-container";
import { ThemingFormSection } from "@containers/common/theming-form-section/theming-form-section";
import { ThemingSubHeaderContainer } from "@containers/common/theming-sub-header-container/theming-sub-header-container";
import { TemplateChooserList } from "@containers/verification-service-details/verification-service-details-tab-container/verification-service-details-theming-tab/theming-messaging-tab-container/template-chooser-list/template-chooser-list";
import { ThemingMessagingTemplateContentForm } from "@containers/verification-service-details/verification-service-details-tab-container/verification-service-details-theming-tab/theming-messaging-tab-container/theming-messaging-template-content-form/theming-messaging-template-content-form";
import { ThemingMessagingTemplatePreview } from "@containers/verification-service-details/verification-service-details-tab-container/verification-service-details-theming-tab/theming-messaging-tab-container/theming-messaging-template-preview/theming-messaging-template-preview";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { MessagingTemplateTypes } from "@enums/messaging-template-types";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { SelectOption } from "@iddataweb/idw-common-ui/types";
import { EmailTemplateFields } from "@idw-types/common/email-template-fields";
import { ThemingMessagingValidationErrors } from "@idw-types/common/validation/common/theming-validation-errors";
import {
    FormattedMessagingTemplate,
    MessagingTemplateConfiguration,
    MessagingTemplate,
    MessagingTemplateCache
} from "@idw-types/data/messaging-template";
import { AxnCompany, AxnRelyingParty } from "@idw-types/data/reference-data/axn";
import { FormattedRpServiceConfiguration } from "@idw-types/data/rp-service-configuration";
import {
    getMessagingTemplateDetails,
    getMessagingTemplateDetailsConfigurations,
    getMessagingTemplatePerTypeOptions,
    getTemplateFileFieldNameForTemplateType,
    getLanguageFileFieldNameForTemplateType,
    getEmailTemplateFieldsJSONValueFromMessagingTemplates
} from "@utils/messaging-template/messaging-template-util";
import { isNullOrUndefined } from "@utils/object-util";
import { AddMessagingTemplateModal } from "@containers/modals/add-messaging-template-modal/add-messaging-template-modal";
import { useModalContext } from "@context/use-modal-context";
import { ModalTypes } from "@enums/modal-types";
import { formatMessagingTemplate, unformatMessagingTemplate } from "@formatters/messaging-templates/messaging-template-formatter";
import { IDWSession } from "@idw-types/data/idw-session";
import { isAxnAdminUser } from "@utils/permissions-util";

export type ThemingMessagingTabContainerProps = {
    session: IDWSession;
    hasUnsubmittedChangeRequest: boolean;
    isDisabled: boolean;
    axnCompanies: Array<AxnCompany>,
    axnRelyingParties: Array<AxnRelyingParty>
    messagingTemplateConfigurations: Array<MessagingTemplateConfiguration>;
    selectedMessagingTemplates: Array<FormattedMessagingTemplate>;
    validationErrors: ThemingMessagingValidationErrors;
    availableMessagingTemplateTypes: Array<MessagingTemplateTypes>;
    rpServiceConfigurationState: FormattedRpServiceConfiguration;
    messagingTemplateCache: MessagingTemplateCache;
    emailTemplateFields: EmailTemplateFields;
    emailFromAddress: string;
    selectedLanguage: LocaleTypes;
    setMessagingTemplateCache: (messagingTemplateCache: MessagingTemplateCache) => void;
    setRpServiceConfigurationState: (rpServiceConfigurationState: FormattedRpServiceConfiguration) => void;
    setHasFetchedMessagingTemplates: (messagingTemplateCache: MessagingTemplateCache) => void;
};

type TemplateTypeRequestInProgressMap = {
    [key in MessagingTemplateTypes]?: boolean;
};

export const ThemingMessagingTabContainer = (props: ThemingMessagingTabContainerProps) => {
    const {
        session,
        hasUnsubmittedChangeRequest,
        isDisabled,
        messagingTemplateConfigurations,
        selectedMessagingTemplates,
        emailTemplateFields,
        validationErrors,
        axnCompanies,
        axnRelyingParties,
        messagingTemplateCache,
        availableMessagingTemplateTypes,
        emailFromAddress,
        rpServiceConfigurationState,
        selectedLanguage,
        setMessagingTemplateCache,
        setRpServiceConfigurationState,
        setHasFetchedMessagingTemplates
    } = props;
    const dispatch = useDispatch();
    const modalContext = useModalContext();
    const [labelWidth] = useState(LabelWidthTypes.EXTRA_SMALL);
    const [layout] = useState(LayoutTypes.COLUMN);
    const [templateTypeRequestInProgressMap, setTemplateTypeRequestInProgressMap] = useState<TemplateTypeRequestInProgressMap>({});
    const [selectedPreviewTemplateUUID, setSelectedPreviewTemplateUUID] = useState(null);

    const updateEmailTemplateFieldsPropertyValue = (newValue: EmailTemplateFields) => {
        const newProperties = rpServiceConfigurationState.properties.map(property => {
            if (property.propertyTypeId === EMAIL_TEMPLATE_FIELDS_PROPERTY_TYPE_ID) {
                return {
                    ...property,
                    value: newValue !== null ? JSON.stringify(newValue) : null,
                    addedToService: newValue !== null
                };
            }
            return property;
        });
        setRpServiceConfigurationState({
            ...rpServiceConfigurationState,
            properties: newProperties
        });
    };

    const userIsAxnAdmin = useMemo(() => {
        return isAxnAdminUser(session.user)
    }, [session.user]);

    useEffect(() => {
        const newFromEmailAddresses: EmailTemplateFields = {};
        //if change request in progress and the emailFromAddress property is configured
        if (!isDisabled && userIsAxnAdmin && emailFromAddress) {
            //if the pinFromEmailAddress field isn't set up - put the emailFromAddress value into it
            if (isNullOrUndefined(emailTemplateFields.pinFromEmailAddress)) {
                newFromEmailAddresses.pinFromEmailAddress = emailFromAddress;
            }
            //if the dcuiFromEmailAddress field isn't set up - put the emailFromAddress value into it
            if (isNullOrUndefined(emailTemplateFields.dcuiFromEmailAddress)) {
                newFromEmailAddresses.dcuiFromEmailAddress = emailFromAddress;
            }

            //if at least one key needs to be updated, store it in the properties
            if (Object.keys(newFromEmailAddresses).length > 0) {
                updateEmailTemplateFieldsPropertyValue({
                    ...emailTemplateFields,
                    ...newFromEmailAddresses
                });
            }
        }
    }, [emailFromAddress, isDisabled]);

    const setTemplateRequestLoadingState = (value: boolean, messagingTemplateType: MessagingTemplateTypes) => {
        setTemplateTypeRequestInProgressMap({
            ...templateTypeRequestInProgressMap,
            [messagingTemplateType]: value
        });
    };

    const handlePinFromEmailAddressChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateEmailTemplateFieldsPropertyValue({
            ...emailTemplateFields,
            [EMAIL_TEMPLATE_FIELDS_PIN_FROM_EMAIL_ADDRESS_PROPERTY_NAME]: event.target.value
        });
    };

    const handleResetPinFromEmailAddress = () => {
        updateEmailTemplateFieldsPropertyValue({
            ...emailTemplateFields,
            [EMAIL_TEMPLATE_FIELDS_PIN_FROM_EMAIL_ADDRESS_PROPERTY_NAME]: DEFAULT_EMAIL_FROM_ADDRESS
        });
    };

    const handleDcuiFromEmailAddressChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateEmailTemplateFieldsPropertyValue({
            ...emailTemplateFields,
            [EMAIL_TEMPLATE_FIELDS_DCUI_FROM_EMAIL_ADDRESS_PROPERTY_NAME]: event.target.value
        });
    };

    const handleResetDcuiFromEmailAddress = () => {
        updateEmailTemplateFieldsPropertyValue({
            ...emailTemplateFields,
            [EMAIL_TEMPLATE_FIELDS_DCUI_FROM_EMAIL_ADDRESS_PROPERTY_NAME]: DEFAULT_EMAIL_FROM_ADDRESS
        });
    };

    const handleTemplateChange = async (selectOption: SelectOption, messagingTemplateType: MessagingTemplateTypes) => {
        if (selectOption !== null) {
            setTemplateRequestLoadingState(true, messagingTemplateType);
            const messagingTemplateConfiguration = messagingTemplateConfigurations.find(mtc => mtc.id === selectOption.value);
            const { id, uuid } = messagingTemplateConfiguration;

            const messagingTemplateCacheObject = await getMessagingTemplateDetails(dispatch)(
                id,
                uuid,
                messagingTemplateCache,
                rpServiceConfigurationState.relyingParty,
                axnCompanies,
                axnRelyingParties
            );

            if (messagingTemplateCacheObject?.formattedMessagingTemplate) {
                const messagingTemplateUUID = messagingTemplateCacheObject.formattedMessagingTemplate.temporaryUUID;
                setMessagingTemplateCache({
                    ...messagingTemplateCache,
                    [messagingTemplateUUID]: messagingTemplateCacheObject
                });

                const newEmailTemplateFields = getEmailTemplateFieldsJSONValueFromMessagingTemplates([messagingTemplateCacheObject.formattedMessagingTemplate]);
                updateEmailTemplateFieldsPropertyValue({
                    ...emailTemplateFields,
                    ...newEmailTemplateFields
                });

                if (!selectedPreviewTemplate || selectedPreviewTemplate.type === messagingTemplateType) {
                    setSelectedPreviewTemplateUUID(messagingTemplateCacheObject.formattedMessagingTemplate.temporaryUUID);
                }
            }

            setTemplateRequestLoadingState(false, messagingTemplateType);
        } else {
            const newSelectedMessagingTemplates = selectedMessagingTemplates.filter(template => template.type !== messagingTemplateType);

            if (selectedPreviewTemplate?.type === messagingTemplateType) {
                if (newSelectedMessagingTemplates.length > 0) {
                    setSelectedPreviewTemplateUUID(newSelectedMessagingTemplates[0].temporaryUUID);
                } else {
                    setSelectedPreviewTemplateUUID(null);
                }
            }

            const templateFileFieldNameToRemove = getTemplateFileFieldNameForTemplateType(messagingTemplateType);
            const languageFileFieldNameToRemove = getLanguageFileFieldNameForTemplateType(messagingTemplateType);
            updateEmailTemplateFieldsPropertyValue({
                ...emailTemplateFields,
                [templateFileFieldNameToRemove]: undefined,
                [languageFileFieldNameToRemove]: undefined
            });
        }
    }

    const handleSelectPreviewTemplateChange = (selectOption: SelectOption<string>) => {
        const messagingTemplate = selectedMessagingTemplates.find(template => template.type === selectOption.value);
        setSelectedPreviewTemplateUUID(messagingTemplate?.temporaryUUID || null);
    }

    const handlePreviewTemplateChange = (templateState: FormattedMessagingTemplate) => {
        const unformattedMessagingTemplate = unformatMessagingTemplate(templateState);
        setMessagingTemplateCache({
            ...messagingTemplateCache,
            [templateState.temporaryUUID]: {
                formattedMessagingTemplate: templateState,
                messagingTemplate: unformattedMessagingTemplate
            }
        });

        setTemplateRequestLoadingState(false, templateState.type);
    }

    const getTemplateTypeRequestInProgressMap = (value: boolean) => {
        return availableMessagingTemplateTypes.reduce((newTemplateTypeRequestInProgressMap, templateType) => {
            const fileName = getTemplateFileFieldNameForTemplateType(templateType);

            if (emailTemplateFields[fileName]) {
                newTemplateTypeRequestInProgressMap[templateType] = value;
            }
            return newTemplateTypeRequestInProgressMap;
        }, {} as TemplateTypeRequestInProgressMap);
    };

    const fetchMessagingTemplates = async () => {
        setTemplateTypeRequestInProgressMap(getTemplateTypeRequestInProgressMap(true));

        const newMessagingTemplateCache = await getMessagingTemplateDetailsConfigurations(dispatch)(
            availableMessagingTemplateTypes,
            messagingTemplateConfigurations,
            emailTemplateFields,
            messagingTemplateCache,
            rpServiceConfigurationState.relyingParty,
            axnCompanies,
            axnRelyingParties
        );

        setMessagingTemplateCache(newMessagingTemplateCache);
        setTemplateTypeRequestInProgressMap(getTemplateTypeRequestInProgressMap(false));

        const cacheKeys = Object.keys(newMessagingTemplateCache);
        if (cacheKeys.length > 0) {
            setSelectedPreviewTemplateUUID(newMessagingTemplateCache[cacheKeys[0]].formattedMessagingTemplate.temporaryUUID);
        }
        setHasFetchedMessagingTemplates(newMessagingTemplateCache);
    }

    const onMessagingTemplateCreated = async (messagingTemplate: MessagingTemplate) => {
        if (availableMessagingTemplateTypes.includes(messagingTemplate.type)) {

            const { uuid } = messagingTemplate;

            const formattedMessagingTemplate = formatMessagingTemplate(messagingTemplate, axnCompanies, axnRelyingParties);
            setMessagingTemplateCache({
                ...messagingTemplateCache,
                [uuid]: {
                    messagingTemplate,
                    formattedMessagingTemplate: formattedMessagingTemplate
                }
            });

            const newEmailTemplateFields = getEmailTemplateFieldsJSONValueFromMessagingTemplates([formattedMessagingTemplate]);
            updateEmailTemplateFieldsPropertyValue({
                ...emailTemplateFields,
                ...newEmailTemplateFields
            });

            setSelectedPreviewTemplateUUID(formattedMessagingTemplate.temporaryUUID);
        }
    }

    const handleCreateMessagingTemplateModal = () => {
        const renderModal = (onModalClose: () => void) => {
            return (
                <AddMessagingTemplateModal
                    isVericationServiceDetailsPage={true}
                    session={session}
                    onModalClose={onModalClose}
                    availableMessagingTemplateTypeOptions={availableMessagingTemplateTypeOptions}
                    onMessagingTemplateCreated={onMessagingTemplateCreated}
                    axnCompanies={company ? [company] : axnCompanies}
                    axnRelyingParties={[rpServiceConfigurationState.relyingParty]}
                    serviceOrganizationId={rpServiceConfigurationState.relyingParty.companyID}
                    serviceRelyingPartyId={rpServiceConfigurationState.relyingParty.id}
                    addToReferenceData={true}
                />
            );
        };

        modalContext.setModalState({
            modalType: ModalTypes.AddMessagingTemplate,
            renderFunction: renderModal
        });
    }

    const availableMessagingTemplateTypeOptions = useMemo(() => {
        return MESSAGING_TEMPLATE_TYPE_OPTIONS.filter(option => availableMessagingTemplateTypes.includes(option.value));
    }, [availableMessagingTemplateTypes]);

    const messagingTemplatePerTypeOptions = useMemo(() => {
        return getMessagingTemplatePerTypeOptions(messagingTemplateConfigurations);
    }, [messagingTemplateConfigurations]);

    useEffect(() => {
        if (emailTemplateFields && selectedMessagingTemplates.length === 0) {
            fetchMessagingTemplates();
        } else {
            setHasFetchedMessagingTemplates(messagingTemplateCache);
        }
    }, [emailTemplateFields]);

    useEffect(() => {
        if (
            selectedMessagingTemplates.length > 0 &&
            availableMessagingTemplateTypeOptions.length > 0 &&
            (!selectedPreviewTemplateUUID || selectedMessagingTemplates.filter(template => template.temporaryUUID === selectedPreviewTemplateUUID).length === 0)
        ) {
            setSelectedPreviewTemplateUUID(selectedMessagingTemplates[0].temporaryUUID);
        }
    }, [
        selectedMessagingTemplates,
        availableMessagingTemplateTypeOptions,
        selectedPreviewTemplateUUID
    ]);

    const pinFromEmailAddress = useMemo(() => {
        if (!isNullOrUndefined(emailTemplateFields.pinFromEmailAddress)) {
            return emailTemplateFields.pinFromEmailAddress
        } else if (!isNullOrUndefined(emailFromAddress)) {
            return emailFromAddress;
        } else {
            return DEFAULT_EMAIL_FROM_ADDRESS;
        }
    }, [emailFromAddress, emailTemplateFields.pinFromEmailAddress]);

    const dcuiFromEmailAddress = useMemo(() => {
        if (!isNullOrUndefined(emailTemplateFields.dcuiFromEmailAddress)) {
            return emailTemplateFields.dcuiFromEmailAddress;
        } else if (!isNullOrUndefined(emailFromAddress)) {
            return emailFromAddress;
        } else {
            return DEFAULT_EMAIL_FROM_ADDRESS;
        }
    }, [emailFromAddress, emailTemplateFields.dcuiFromEmailAddress]);

    const previewTemplatesOptions = useMemo(() => {
        return Object.values(selectedMessagingTemplates).map(template => ({
            label: template.name,
            value: template.type
        }
        ));
    }, [selectedMessagingTemplates]);

    const selectedPreviewTemplate = useMemo(() => {
        return selectedMessagingTemplates.find(mt => mt.temporaryUUID === selectedPreviewTemplateUUID) || null;
    }, [selectedMessagingTemplates, selectedPreviewTemplateUUID]);

    const selectedPreviewTemplateOption = useMemo(() => {
        if (selectedPreviewTemplate) {
            return {
                label: selectedPreviewTemplate.name,
                value: selectedPreviewTemplate.type
            };
        }

        return null;
    }, [selectedPreviewTemplate]);

    const company = useMemo(() => {
        if (rpServiceConfigurationState.relyingParty) {
            return axnCompanies.find(company => company.id === rpServiceConfigurationState.relyingParty.companyID);
        }

        return null;
    }, [axnCompanies, rpServiceConfigurationState.relyingParty]);

    const messagingTemplateResources = resources.pages.verificationServiceDetails.tabs.theming.subTabs.messaging;

    return (
        <div>
            <ContentPageHeader title={messagingTemplateResources.title} />
            <ContentPageBody isMaxWidth={true}>
                <ThemingSubHeaderContainer description={messagingTemplateResources.description} />
                <ThemingConfigurationContainer>
                    <ThemingFormContainer>
                        <>
                            <TemplateChooserList
                                isDisabled={isDisabled || !session.userPermissions.canCreateMessagingTemplates}
                                messagingTemplatePerTypeOptions={messagingTemplatePerTypeOptions}
                                availableMessagingTemplateTypeOptions={availableMessagingTemplateTypeOptions}
                                templateTypeRequestInProgressMap={templateTypeRequestInProgressMap}
                                selectedMessagingTemplates={selectedMessagingTemplates}
                                setSelectedTemplate={handleTemplateChange}
                                validationWarnings={validationErrors.templateWarnings}
                            />
                            {Object.keys(selectedMessagingTemplates).length > 0 && selectedPreviewTemplate && (
                                <>
                                    {selectedPreviewTemplate.type === MessagingTemplateTypes.PIN_EMAIL && (
                                        <ThemingFormSection title={messagingTemplateResources.sections.emailFromAddress.title}>
                                            <>
                                                <InputFormGroup
                                                    controlId={"theming-messaging-pin-email-from-address-input"}
                                                    automationId={"theming-messaging-pin-email-from-address-input"}
                                                    label={messagingTemplateResources.sections.emailFromAddress.inputs.pinFromEmailAddress.label}
                                                    ariaLabel={messagingTemplateResources.sections.emailFromAddress.inputs.pinFromEmailAddress.label}
                                                    layout={layout}
                                                    labelWidth={labelWidth}
                                                    onChange={handlePinFromEmailAddressChange}
                                                    value={pinFromEmailAddress || ""}
                                                    disabled={isDisabled || !userIsAxnAdmin}
                                                    isRequired={true}
                                                    afterLabelTooltipText={messagingTemplateResources.sections.emailFromAddress.inputs.pinFromEmailAddress.tooltip}
                                                    afterLabelIcon={(isDisabled || !userIsAxnAdmin) ? null : faUndo}
                                                    afterLabelIconOnClick={handleResetPinFromEmailAddress}
                                                />
                                                {validationErrors.pinFromEmailAddressError}
                                            </>
                                        </ThemingFormSection>
                                    )}
                                    {selectedPreviewTemplate.type === MessagingTemplateTypes.DCUI_EMAIL && (
                                        <ThemingFormSection title={messagingTemplateResources.sections.emailFromAddress.title}>
                                            <>
                                                <InputFormGroup
                                                    controlId={"theming-messaging-dcui-email-from-address-input"}
                                                    automationId={"theming-messaging-dcui-email-from-address-input"}
                                                    label={messagingTemplateResources.sections.emailFromAddress.inputs.dcuiFromEmailAddress.label}
                                                    ariaLabel={messagingTemplateResources.sections.emailFromAddress.inputs.dcuiFromEmailAddress.label}
                                                    layout={layout}
                                                    labelWidth={labelWidth}
                                                    onChange={handleDcuiFromEmailAddressChange}
                                                    value={dcuiFromEmailAddress || ""}
                                                    disabled={isDisabled || !userIsAxnAdmin}
                                                    isRequired={true}
                                                    afterLabelTooltipText={messagingTemplateResources.sections.emailFromAddress.inputs.dcuiFromEmailAddress.tooltip}
                                                    afterLabelIcon={(isDisabled || !userIsAxnAdmin) ? null : faUndo}
                                                    afterLabelIconOnClick={handleResetDcuiFromEmailAddress}
                                                />
                                                {validationErrors.dcuiFromEmailAddressError}
                                            </>
                                        </ThemingFormSection>
                                    )}
                                    <ThemingMessagingTemplateContentForm
                                        isDisabled={isDisabled}
                                        messagingTemplate={selectedPreviewTemplate}
                                        selectedLanguage={selectedLanguage}
                                        validationErrors={validationErrors.templateErrors[selectedPreviewTemplate?.type] || null}
                                        setSelectedLanguage={null}
                                        labelWidth={labelWidth}
                                        setMessagingTemplateState={handlePreviewTemplateChange}
                                    />
                                </>

                            )}
                            {(hasUnsubmittedChangeRequest && session.userPermissions.canCreateMessagingTemplates && rpServiceConfigurationState.permissions.canModify) && (
                                <Button disabled={isDisabled} onClick={handleCreateMessagingTemplateModal} >{"Create Messaging Template"}</Button>
                            )}
                        </>
                    </ThemingFormContainer>

                    {Object.keys(selectedMessagingTemplates).length > 0 && <div style={{ width: "100%" }}>
                        <ThemingMessagingTemplatePreview
                            state={selectedPreviewTemplate}
                            selectedLanguage={selectedLanguage}
                            previewTemplatesOptions={previewTemplatesOptions}
                            selectedPreviewTemplateOption={selectedPreviewTemplateOption}
                            handleSelectPreviewTemplateChange={handleSelectPreviewTemplateChange}
                        />
                    </div>
                    }
                </ThemingConfigurationContainer>
            </ContentPageBody>
        </div >
    );
};